const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://photo-x-yash-bhai.onrender.com/api/v1"
    : "http://localhost:4100/api/v1";
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");
const configuredRequestTimeoutMs = Number(
  process.env.REACT_APP_API_REQUEST_TIMEOUT_MS,
);
const API_REQUEST_TIMEOUT_MS =
  Number.isFinite(configuredRequestTimeoutMs) &&
  configuredRequestTimeoutMs >= 1000
    ? configuredRequestTimeoutMs
    : 60000;

const USER_KEY = "photox.admin.user";
const LEGACY_TOKEN_KEY = "photox.admin.idToken";

export class ApiError extends Error {
  constructor(message, { status, code, requestId, details } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

export function getStoredSession() {
  const serializedUser =
    window.localStorage.getItem(USER_KEY) ||
    window.sessionStorage.getItem(USER_KEY);
  if (!serializedUser) return null;

  try {
    const user = JSON.parse(serializedUser);
    window.localStorage.setItem(USER_KEY, serializedUser);
    window.sessionStorage.removeItem(USER_KEY);
    return { user };
  } catch {
    clearStoredSession();
    return null;
  }
}

export function storeSession({ user }) {
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  window.sessionStorage.removeItem(USER_KEY);
}

export function clearStoredSession() {
  window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
  window.sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  window.sessionStorage.removeItem(USER_KEY);
}

async function apiRequest(
  path,
  { token, method = "GET", body, signal, acceptStatuses = [] } = {},
) {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", abortFromCaller, { once: true });
  }
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, API_REQUEST_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      signal: controller.signal,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    if (timedOut) {
      throw new ApiError(
        `Request timed out after ${Math.round(API_REQUEST_TIMEOUT_MS / 1000)} seconds`,
        {
          code: "REQUEST_TIMEOUT",
        },
      );
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener("abort", abortFromCaller);
  }

  let payload;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (
    (!response.ok && !acceptStatuses.includes(response.status)) ||
    payload?.success === false
  ) {
    const error = payload?.error;
    const validationDetails = Array.isArray(error?.details)
      ? error.details
          .map((detail) =>
            [detail.path, detail.message].filter(Boolean).join(": "),
          )
          .join("; ")
      : "";
    throw new ApiError(
      [
        error?.message || `Request failed with status ${response.status}`,
        validationDetails,
      ]
        .filter(Boolean)
        .join(" — "),
      {
        status: response.status,
        code: error?.code,
        requestId: payload?.requestId,
        details: error?.details,
      },
    );
  }

  return payload;
}

export async function requestAdminOtp(email) {
  const payload = await apiRequest("/auth/otp/request", {
    method: "POST",
    body: {
      email,
      audience: "admin",
      ...(globalThis.location?.href
        ? { returnUrl: globalThis.location.href }
        : {}),
    },
  });
  return payload.data;
}

export async function verifyAdminOtp(email, code) {
  const payload = await apiRequest("/auth/otp/verify", {
    method: "POST",
    body: { email, code, audience: "admin" },
  });
  return payload.data;
}

export async function logout(token) {
  try {
    await apiRequest("/auth/logout", { method: "POST", token });
  } finally {
    clearStoredSession();
  }
}

export function loadAdminDashboard(token, signal) {
  return Promise.all([
    apiRequest("/admin/operations/overview", { token, signal }),
    apiRequest("/events?limit=100&sort=newest", { token, signal }),
    apiRequest("/studios?limit=100&status=active", { token, signal }),
    apiRequest("/admin/operations/moderation?status=open&limit=25", {
      token,
      signal,
    }),
    apiRequest("/admin/operations/retention?state=all&limit=25", {
      token,
      signal,
    }),
    apiRequest("/admin/operations/reports/subscriptions", { token, signal }),
    apiRequest("/admin/photographers?limit=100", { token, signal }),
    apiRequest("/admin/event-types?limit=100", { token, signal }),
    apiRequest("/billing/plans", { token, signal }),
  ]).then(
    ([
      overview,
      events,
      studios,
      moderation,
      retention,
      subscriptions,
      photographers,
      eventTypes,
      billingPlans,
    ]) => ({
      overview: overview.data,
      events: events.data,
      studios: studios.data,
      moderation: moderation.data,
      retention: retention.data,
      subscriptions: subscriptions.data,
      photographers: photographers.data,
      eventTypes: eventTypes.data,
      plans: billingPlans.data.plans,
      health: null,
    }),
  );
}

export async function loadAdminHealth(token, signal) {
  const health = await apiRequest("/admin/operations/health", {
    token,
    signal,
    acceptStatuses: [503],
  });
  return health.data;
}

export async function loadStudioMembers(token, studioId) {
  const payload = await apiRequest(`/studios/${studioId}/members`, { token });
  return payload.data;
}

export async function createStudio(token, values) {
  const payload = await apiRequest("/studios", {
    token,
    method: "POST",
    body: values,
  });
  return payload.data.studio;
}

export async function updateStudio(token, studioId, values) {
  const payload = await apiRequest(`/studios/${studioId}`, {
    token,
    method: "PATCH",
    body: values,
  });
  return payload.data.studio;
}

export async function closeStudio(token, studioId) {
  const payload = await apiRequest(`/studios/${studioId}`, {
    token,
    method: "DELETE",
  });
  return payload.data;
}

export async function addStudioPhotographer(
  token,
  studioId,
  photographerId,
  role = "photographer",
) {
  const payload = await apiRequest(`/studios/${studioId}/members`, {
    token,
    method: "POST",
    body: { photographerId, role },
  });
  return payload.data;
}

export async function removeStudioMember(token, studioId, membershipId) {
  const payload = await apiRequest(
    `/studios/${studioId}/members/${membershipId}`,
    {
      token,
      method: "DELETE",
    },
  );
  return payload.data.membership;
}

export async function loadEventPhotoDelivery(
  token,
  eventId,
  photoId,
  variant = "preview",
  signal,
) {
  const payload = await apiRequest(
    `/events/${eventId}/photos/${photoId}/media?variant=${variant}`,
    { token, signal },
  );
  return payload.data.delivery;
}

export async function loadEventGallery(token, eventId, signal) {
  const gallery = await apiRequest(
    `/events/${eventId}/gallery?scope=all&limit=100`,
    {
      token,
      signal,
    },
  );
  return Promise.all(
    gallery.data.map(async (photo) => {
      const variant = photo.preview?.available
        ? "preview"
        : photo.thumbnail?.available
          ? "thumbnail"
          : photo.original?.available
            ? "original"
            : null;
      if (!variant) return { ...photo, mediaUrl: null };
      try {
        const delivery = await loadEventPhotoDelivery(
          token,
          eventId,
          photo.id,
          variant,
          signal,
        );
        return { ...photo, mediaUrl: delivery.url };
      } catch {
        return { ...photo, mediaUrl: null };
      }
    }),
  );
}

export async function createEvent(token, values) {
  const payload = await apiRequest("/events", {
    token,
    method: "POST",
    body: values,
  });
  return payload.data;
}

export async function loadPlanCatalog(token) {
  const payload = await apiRequest("/admin/billing-plans", { token });
  return payload.data.plans;
}

export async function createPlanVersion(token, code, values) {
  const payload = await apiRequest(`/admin/billing-plans/${code}/versions`, {
    token,
    method: "POST",
    body: values,
  });
  return payload.data.plan;
}

export async function publishPlanVersion(
  token,
  code,
  version,
  expectedRevision,
) {
  const payload = await apiRequest(
    `/admin/billing-plans/${code}/versions/${version}/publish`,
    { token, method: "POST", body: { expectedRevision } },
  );
  return payload.data.plan;
}

export async function unpublishPlan(token, code, expectedRevision) {
  const payload = await apiRequest(`/admin/billing-plans/${code}/unpublish`, {
    token,
    method: "POST",
    body: { expectedRevision },
  });
  return payload.data.plan;
}

export async function archivePlan(token, code, expectedRevision) {
  const payload = await apiRequest(`/admin/billing-plans/${code}`, {
    token,
    method: "DELETE",
    body: { expectedRevision },
  });
  return payload.data.plan;
}

export async function updateEvent(token, eventId, values) {
  const payload = await apiRequest(`/events/${eventId}`, {
    token,
    method: "PATCH",
    body: values,
  });
  return payload.data.event;
}

export async function updateEventAssignments(token, eventId, values) {
  const payload = await apiRequest(`/events/${eventId}/assignments`, {
    token,
    method: "PATCH",
    body: values,
  });
  return payload.data.event;
}

export async function loadEventPhotographerApplications(token, eventId) {
  const payload = await apiRequest(
    `/events/${encodeURIComponent(eventId)}/photographer-applications`,
    { token },
  );
  return payload.data.applications || [];
}

export async function reviewEventPhotographerApplication(
  token,
  eventId,
  applicationId,
  status,
  reviewNote,
) {
  const payload = await apiRequest(
    `/events/${encodeURIComponent(eventId)}/photographer-applications/${encodeURIComponent(applicationId)}`,
    {
      method: "PATCH",
      token,
      body: { status, ...(reviewNote ? { reviewNote } : {}) },
    },
  );
  return payload.data.application;
}

export async function loadPartnerDirectory(token, filters = {}, signal) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const payload = await apiRequest(
    `/admin/photographers/directory?${params.toString()}`,
    { token, signal },
  );
  return { profiles: payload.data, pagination: payload.meta };
}

export async function transitionEvent(token, eventId, status) {
  const payload = await apiRequest(`/events/${eventId}/transition`, {
    token,
    method: "POST",
    body: { status },
  });
  return payload.data.event;
}

export async function archiveEvent(token, eventId) {
  const payload = await apiRequest(`/events/${eventId}/archive`, {
    token,
    method: "POST",
  });
  return payload.data.event;
}

export async function deleteEvent(token, eventId) {
  const payload = await apiRequest(`/events/${eventId}`, {
    token,
    method: "DELETE",
  });
  return payload.data;
}

export async function deletePhotographer(token, profileId) {
  const payload = await apiRequest(`/admin/photographers/${profileId}`, {
    token,
    method: "DELETE",
  });
  return payload.data;
}

export async function publishEvent(token, eventId) {
  const payload = await apiRequest(`/events/${eventId}/publish`, {
    token,
    method: "POST",
  });
  return payload.data.event;
}

export async function sponsorEventPlan(token, eventId, planCode) {
  const payload = await apiRequest(
    `/admin/operations/events/${eventId}/sponsor-plan`,
    {
      token,
      method: "POST",
      body: planCode ? { planCode } : undefined,
    },
  );
  return payload.data;
}

export async function rotateEventAccess(token, eventId) {
  const payload = await apiRequest(`/events/${eventId}/access/rotate`, {
    token,
    method: "POST",
    body: {},
  });
  return payload.data.access;
}

export async function resolveModeration(token, caseId, status) {
  const payload = await apiRequest(`/admin/operations/moderation/${caseId}`, {
    token,
    method: "PATCH",
    body: {
      status,
      resolutionNote: `Resolved from the PhotoX admin panel as ${status}`,
    },
  });
  return payload.data.moderationCase;
}

export async function reviewPhotographer(token, profileId, status, reason) {
  const payload = await apiRequest(`/admin/photographers/${profileId}/status`, {
    token,
    method: "PATCH",
    body: { status, ...(reason ? { reason } : {}) },
  });
  return payload.data.profile;
}

export async function createPhotographer(token, values) {
  const payload = await apiRequest("/admin/photographers", {
    token,
    method: "POST",
    body: values,
  });
  return payload.data.profile;
}

export async function createEventType(token, values) {
  const payload = await apiRequest("/admin/event-types", {
    token,
    method: "POST",
    body: values,
  });
  return payload.data.eventType;
}

export async function updateEventType(token, eventTypeId, values) {
  const payload = await apiRequest(`/admin/event-types/${eventTypeId}`, {
    token,
    method: "PATCH",
    body: values,
  });
  return payload.data.eventType;
}

export async function archiveEventType(token, eventTypeId) {
  const payload = await apiRequest(`/admin/event-types/${eventTypeId}`, {
    token,
    method: "DELETE",
  });
  return payload.data.eventType;
}

export { API_BASE_URL };

// Bulk uploads and Drive imports use the existing authenticated, event-scoped media APIs.
export function adminMediaApi(token) {
  const call = (path, options = {}) => apiRequest(path, { token, ...options });
  return {
    createBulk: async (eventId, files, idempotencyKey) => {
      const result = await call(`/events/${eventId}/bulk-uploads`, {
        method: "POST",
        body: { files, idempotencyKey },
      });
      return result.data;
    },
    initiate: async (eventId, metadata) =>
      (
        await call(`/events/${eventId}/uploads`, {
          method: "POST",
          body: metadata,
        })
      ).data,
    signPart: async (eventId, photoId, partNumber, signal) =>
      (
        await call(`/events/${eventId}/uploads/${photoId}/parts`, {
          method: "POST",
          body: { partNumber },
          signal,
        })
      ).data.part,
    complete: async (eventId, photoId, body, signal) =>
      (
        await call(`/events/${eventId}/uploads/${photoId}/complete`, {
          method: "POST",
          body,
          signal,
        })
      ).data,
    abort: async (eventId, photoId) =>
      (
        await call(`/events/${eventId}/uploads/${photoId}`, {
          method: "DELETE",
        })
      ).data,
    remove: async (eventId, photoId) =>
      (
        await call(`/events/${eventId}/photos/${photoId}/media`, {
          method: "DELETE",
        })
      ).data,
    importDrive: async (eventId, values) =>
      (
        await call(`/events/${eventId}/drive-imports`, {
          method: "POST",
          body: values,
        })
      ).data.session,
    jobStatus: async (eventId, jobId, type) =>
      (
        await call(
          `/events/${eventId}/${type === "drive" ? "drive-imports" : "bulk-uploads"}/${jobId}`,
        )
      ).data.session,
  };
}
